import { Activity, useState } from 'react'
import {
  Add,
  DeleteTwoTone,
  ExpandLessTwoTone,
  ExpandMoreTwoTone,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import {
  emptyRecord,
  evaluateCondition,
  updateAtIndex,
} from '../../utilities/fieldUtils'

import LabelValueRow from './LabelValueRow'
import { useScrollToNew } from '../../hooks/useScrollToNew'

const DetailCard = ({
  isInEditMode,
  title,
  collapsible,
  update,
  data,
  repeater,
  fields = [],
  fieldgroups = [],
  addButtonLabel,
  addSection,
  removeSection,
  errors,
  responsibleRoles,
  ref,
}) => {
  const [expanded, setExpanded] = useState(true)
  const [expandedGroups, setExpandedGroups] = useState({})
  const { setNewItemKey, scrollRefFor } = useScrollToNew()

  const renderFields = (fieldList, itemData = data, itemUpdate = update) =>
    fieldList
      .filter(
        (f) =>
          !f.hidden &&
          (!f.condition || evaluateCondition(f.condition, itemData)),
      )
      .map((field) => {
        if (field.type === 'dependent') {
          const parentValue = itemData?.[field.parent]
          return {
            ...field,
            value:
              parentValue != null ? (field.map?.[parentValue] ?? null) : null,
          }
        }
        return { ...field, value: itemData?.[field.key] ?? null }
      })
      .map((field, index) => {
        const error = errors?.find((e) => e.field === field.key)
        return (
          <LabelValueRow
            {...field}
            key={index}
            responsibleRoles={field.allowedRoles ?? responsibleRoles}
            isInEditMode={isInEditMode}
            onChange={(value) => itemUpdate?.(field.key, value)}
            error={!!error}
            helperText={error?.message}
          />
        )
      })

  const addGroupItem = (group) => {
    const items = data?.[group.key] ?? []
    setNewItemKey(`${group.key}-${items.length}`)
    update?.(group.key, [...items, emptyRecord(group.fields)])
  }

  const removeGroupItem = (group, index) => {
    const updated = (data?.[group.key] ?? []).filter((_, i) => i !== index)
    update?.(group.key, updated)
  }

  return (
    <Card ref={ref} elevation={1} sx={{ width: '100%' }}>
      <CardHeader
        title={title}
        slotProps={{ title: { variant: 'h6', component: 'h2' } }}
        action={
          <Stack gap={1} direction={'row'}>
            <Activity mode={repeater && isInEditMode ? 'visible' : 'hidden'}>
              <Button
                onClick={() => addSection()}
                size="small"
                variant="outlined"
                aria-label="add item"
                startIcon={<Add></Add>}
              >
                {addButtonLabel}
              </Button>
            </Activity>
            <Activity
              mode={removeSection && isInEditMode ? 'visible' : 'hidden'}
            >
              <IconButton
                size="small"
                color="error"
                aria-label="Remove section"
                onClick={removeSection}
              >
                <DeleteTwoTone />
              </IconButton>
            </Activity>
            <Activity mode={collapsible ? 'visible' : 'hidden'}>
              <IconButton
                onClick={() => setExpanded((prev) => !prev)}
                size="small"
                aria-label={expanded ? 'Collapse' : 'Expand'}
              >
                {expanded ? <ExpandLessTwoTone /> : <ExpandMoreTwoTone />}
              </IconButton>
            </Activity>
          </Stack>
        }
      />
      <Collapse in={!collapsible || expanded}>
        <CardContent>
          <Stack spacing={2}>
            {renderFields(fields)}
            {fieldgroups.map((group) => {
              if (group.repeater) {
                const items = data?.[group.key] ?? []
                return items.map((item, index) => {
                  const expandKey = `${group.key}-${index}`
                  const isLast = index === items.length - 1
                  const itemUpdate = (key, value) => {
                    update?.(
                      group.key,
                      updateAtIndex(items, index, { [key]: value }),
                    )
                  }
                  return (
                    <Box key={expandKey} ref={scrollRefFor(expandKey)}>
                      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                        <IconButton
                          size="small"
                          aria-label={
                            expandedGroups[expandKey] !== false
                              ? 'Collapse'
                              : 'Expand'
                          }
                          onClick={() =>
                            setExpandedGroups((prev) => ({
                              ...prev,
                              [expandKey]: prev[expandKey] === false,
                            }))
                          }
                        >
                          {expandedGroups[expandKey] !== false ? (
                            <ExpandLessTwoTone />
                          ) : (
                            <ExpandMoreTwoTone />
                          )}
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium', color: 'text.primary' }}
                        >
                          {group.label}
                          {items.length > 1 ? ` ${index + 1}` : ''}
                        </Typography>
                        {isInEditMode && (
                          <Stack direction="row" sx={{ ml: 'auto' }}>
                            {items.length > 1 && index > 0 && (
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Remove item"
                                onClick={() => removeGroupItem(group, index)}
                              >
                                <DeleteTwoTone />
                              </IconButton>
                            )}
                            {isLast && (
                              <IconButton
                                size="small"
                                aria-label="Add item"
                                onClick={() => addGroupItem(group)}
                              >
                                <Add />
                              </IconButton>
                            )}
                          </Stack>
                        )}
                      </Stack>
                      <Collapse in={expandedGroups[expandKey] !== false}>
                        <Stack spacing={2} ml={2}>
                          {renderFields(group.fields, item, itemUpdate)}
                        </Stack>
                      </Collapse>
                    </Box>
                  )
                })
              }

              return (
                <Box key={group.key}>
                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <IconButton
                      size="small"
                      aria-label={
                        expandedGroups[group.key] !== false
                          ? 'Collapse'
                          : 'Expand'
                      }
                      onClick={() =>
                        setExpandedGroups((prev) => ({
                          ...prev,
                          [group.key]: prev[group.key] === false,
                        }))
                      }
                    >
                      {expandedGroups[group.key] !== false ? (
                        <ExpandLessTwoTone />
                      ) : (
                        <ExpandMoreTwoTone />
                      )}
                    </IconButton>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'medium', color: 'text.primary' }}
                    >
                      {group.label}
                    </Typography>
                  </Stack>
                  <Collapse in={expandedGroups[group.key] !== false}>
                    <Stack spacing={2} ml={2}>
                      {renderFields(group.fields)}
                    </Stack>
                  </Collapse>
                </Box>
              )
            })}
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  )
}

export default DetailCard
